import * as Tone from 'tone';
import { state } from './state.js';
import { GAME_SPEED_MULTIPLIER } from './constants.js';
import backingTrackUrl from './assets/backingtrack_2.mp3';

// --- Japanese Translation Vocoder V/O Registry ---
export const JAPANESE_VO_MAP = {
    "Space Golf, START!": "スペースゴルフ、スタート！",
    "GG! GOOD GAME!": "ジージー！グッドゲーム！",
    "GAME OVER!": "ゲームオーバー！",
    "SINGLE PLAYER!": "シングルプレイヤー！",
    "MAIN MENU!": "メインメニュー！",
    "POWER UP SHOP!": "パワーアップショップ！",
    "SETTINGS!": "セッティング！",
    "RESUME!": "レジューム！",
    "MULTIPLAYER SPEEDRUN!": "マルチプレイヤー！",
    "1V1 SPEEDRUN!": "チャレンジ！",
    "ARCADE MODE!": "アーケードモード！",
    "GAME ON!": "ゲームオン！",
    "Arcade, START!": "アーケード、スタート！",
    "GAMBATTE!": "がんばって！",
    "READY, GO!": "レディーゴー！",
    "IKUZO!": "いくぞ！"
};

// --- Dedicated Space Reverb and Echo Delay for gameplay sound effects ---
export const sfxReverb = new Tone.Reverb({
    roomSize: 0.65,
    dampening: 3000,
    wet: 0.32
}).toDestination();

export const sfxDelay = new Tone.FeedbackDelay({
    delayTime: "16n.",
    feedback: 0.35,
    wet: 0.25
}).connect(sfxReverb);

// --- Synthesizers Setup ---
export const synth = new Tone.Synth().connect(sfxDelay);

// Bounces routed directly to reverb (no delay) and set to comfortable lower volume
export const thudBounceSynth = new Tone.MembraneSynth({
    pitchDecay: 0.02,
    octaves: 6,
    oscillator: { type: "sine" },
    envelope: { attack: 0.001, decay: 0.1, sustain: 0.005, release: 0.2, attackCurve: "exponential" }
}).connect(sfxReverb);
thudBounceSynth.volume.value = -19; 

// Collisions routed directly to reverb (no delay) and set to comfortable lower volume
export const kick = new Tone.MembraneSynth({ 
    pitchDecay: 0.05, 
    octaves: 10, 
    oscillator: { type: "sine" }, 
    envelope: { attack: 0.001, decay: 0.4, sustain: 0.01, release: 1.4, attackCurve: "exponential"} 
}).connect(sfxReverb);
kick.volume.value = -13;

// Synthesized flagpole/hole chiptune clink hit sound routed directly to reverb
export const goalClinkSynth = new Tone.Synth({
    oscillator: { type: "square" },
    envelope: { attack: 0.001, decay: 0.08, sustain: 0, release: 0.08 }
}).connect(sfxReverb);
goalClinkSynth.volume.value = -15;

export const goalSound = {
    triggerAttackRelease: function(note, duration, time) {
        if (state.IS_MUTED) return;
        try {
            const playTime = time || Tone.now();
            goalClinkSynth.triggerAttackRelease("C6", "32n", playTime);
            goalClinkSynth.triggerAttackRelease("G6", "32n", playTime + 0.05);
        } catch (e) {
            console.error("goalSound clink trigger failed:", e);
        }
    }
};

export const buySound = new Tone.Synth({
    oscillator: {type: "square"}, 
    envelope: {attack: 0.005, decay: 0.1, sustain: 0, release: 0.1}
}).connect(sfxDelay);
buySound.volume.value = -15;

export const activatePowerUpSound = new Tone.Synth({
    oscillator: {type: "triangle"}, 
    envelope: {attack: 0.01, decay: 0.05, sustain: 0.02, release: 0.1}
}).connect(sfxDelay);
activatePowerUpSound.volume.value = -10;

export const openShopSound = new Tone.Synth({
    oscillator: {type: "sine"}, 
    envelope: {attack: 0.005, decay: 0.1, sustain: 0.05, release: 0.1}
}).connect(sfxDelay);
openShopSound.volume.value = -15;

export const cannotAffordSound = new Tone.Synth({
    oscillator: {type: "sawtooth"}, 
    envelope: {attack: 0.01, decay: 0.2, sustain: 0, release: 0.1}
}).connect(sfxDelay);
cannotAffordSound.volume.value = -18;

export const sandTrapSound = new Tone.NoiseSynth({
    noise: { type: "brown" },
    envelope: { attack: 0.005, decay: 0.15, sustain: 0, release: 0.1 }
}).connect(sfxReverb);
sandTrapSound.volume.value = -15;

export function playBuySuccessSound() {
    if (state.IS_MUTED) return;
    try {
        if (Tone.context.state !== 'running') {
            Tone.start().catch(e => console.error("Tone.start failed in playBuySuccessSound:", e));
        }
        const now = Tone.now();
        // Ascending retro chimes: C5, E5, G5, C6
        buySound.triggerAttackRelease("C5", "32n", now);
        buySound.triggerAttackRelease("E5", "32n", now + 0.05);
        buySound.triggerAttackRelease("G5", "32n", now + 0.10);
        buySound.triggerAttackRelease("C6", "16n", now + 0.15);
    } catch (e) {
        console.error("playBuySuccessSound failed:", e);
    }
}

export function playBuyFailureSound() {
    if (state.IS_MUTED) return;
    try {
        if (Tone.context.state !== 'running') {
            Tone.start().catch(e => console.error("Tone.start failed in playBuyFailureSound:", e));
        }
        const now = Tone.now();
        // Descending buzzy retro chimes: C3, Ab2, F2
        cannotAffordSound.triggerAttackRelease("C3", "16n", now);
        cannotAffordSound.triggerAttackRelease("Ab2", "16n", now + 0.08);
        cannotAffordSound.triggerAttackRelease("F2", "8n", now + 0.16);
    } catch (e) {
        console.error("playBuyFailureSound failed:", e);
    }
}

// --- Sound Play Orchestrator ---
export function playSound(soundSynth, note, duration, time, velocity) {
    if (!state.IS_MUTED && soundSynth) { 
        try {
            if (soundSynth instanceof Tone.Player) { 
                 if (soundSynth.loaded) soundSynth.start(time);
            } else if (soundSynth instanceof Tone.NoiseSynth) {
                soundSynth.triggerAttackRelease(duration, time);
            } else if (velocity !== undefined) { 
                soundSynth.triggerAttackRelease(note, duration, time, velocity);
            } else { 
                soundSynth.triggerAttackRelease(note, duration, time);
            }
        } catch (e) {
            console.error("Tone.js error:", e);
        }
    }
}

// --- Audio Bootstrap Context Helper ---
export function checkAudioContext() {
    if (state.IS_MUTED) {
        return Promise.resolve(false);
    }
    if (Tone.context.state !== 'running') {
        return Tone.start()
            .then(() => {
                console.log("Tone.js context started successfully.");
                return true;
            })
            .catch(e => {
                console.error("Error starting Tone.js context:", e);
                return false;
            });
    }
    return Promise.resolve(true);
}

export function syncMuteState() {
    Tone.Destination.mute = state.IS_MUTED;
}

// --- 90s Retro Arcade Synthesizers & Voice Speech Synthesis ---
export const retroArcadeStartSound = new Tone.PolySynth(Tone.Synth, {
    oscillator: { type: "square" },
    envelope: { attack: 0.005, decay: 0.1, sustain: 0.2, release: 0.15 }
}).connect(sfxDelay);
retroArcadeStartSound.volume.value = -12;

export const retroArcadeWinSound = new Tone.PolySynth(Tone.Synth, {
    oscillator: { type: "triangle" },
    envelope: { attack: 0.005, decay: 0.15, sustain: 0.3, release: 0.25 }
}).connect(sfxDelay);
retroArcadeWinSound.volume.value = -10;

// Sticker slap/stamp synthesized cartoon frequency sweep
export const stickerSlapSound = new Tone.Synth({
    oscillator: { type: "sine" },
    envelope: { attack: 0.001, decay: 0.12, sustain: 0, release: 0.08 }
}).connect(sfxDelay);
stickerSlapSound.volume.value = -6;

export function playStickerSlap() {
    if (state.IS_MUTED) return;
    try {
        const now = Tone.now();
        stickerSlapSound.triggerAttackRelease("C6", "16n", now);
        stickerSlapSound.frequency.setValueAtTime(1046.50, now); // C6 frequency
        stickerSlapSound.frequency.exponentialRampToValueAtTime(130.81, now + 0.1); // C3 frequency
    } catch (e) {
        console.error("Sticker slap sound failure:", e);
    }
}

// Web Speech API robotic vocal vocoder mimicry
let activeUtterances = [];

export function playRoboticVoice(text) {
    if (state.IS_MUTED) return;
    try {
        // Resume in case it got stuck in a paused state (browser speech engine bug workaround)
        window.speechSynthesis.resume();
        window.speechSynthesis.cancel();
        
        // Translate text if match found
        const normalizedText = text.trim();
        const translatedText = JAPANESE_VO_MAP[normalizedText] || normalizedText;
        
        const utterance = new SpeechSynthesisUtterance(translatedText);
        utterance.volume = 0.85;
        utterance.rate = 1.08; // slightly sped up for punchy retro console feeling
        utterance.pitch = 0.52; // deep, flat robotic voice pitch
        utterance.lang = 'ja-JP'; // Request Japanese V/O engine
        
        // Pick Japanese system vocal profile
        const voices = window.speechSynthesis.getVoices();
        if (voices && voices.length > 0) {
            const jaVoice = voices.find(v => v.lang.startsWith('ja-JP') || v.lang.startsWith('ja'));
            if (jaVoice) {
                utterance.voice = jaVoice;
            }
        }
        
        // Prevent speech engine garbage collection bug in Chrome/Safari
        activeUtterances.push(utterance);
        utterance.onend = () => {
            activeUtterances = activeUtterances.filter(u => u !== utterance);
        };
        utterance.onerror = () => {
            activeUtterances = activeUtterances.filter(u => u !== utterance);
        };
        
        window.speechSynthesis.speak(utterance);
    } catch (e) {
        console.error("Speech Synthesis failure:", e);
    }
}

export function triggerStartArcadeSequence() {
    if (state.IS_MUTED) return;
    try {
        const now = Tone.now();
        // 8-bit square chiptune arpeggio
        retroArcadeStartSound.triggerAttackRelease("C4", "16n", now);
        retroArcadeStartSound.triggerAttackRelease("E4", "16n", now + 0.08);
        retroArcadeStartSound.triggerAttackRelease("G4", "16n", now + 0.16);
        retroArcadeStartSound.triggerAttackRelease("C5", "8n", now + 0.24);
        
        // Shout robotic voice
        setTimeout(() => {
            playRoboticVoice("Space Golf, START!");
        }, 120);
    } catch (e) {
        console.error("Arcade Start Sound failure:", e);
    }
}

export function triggerStartArcadeModeSequence() {
    if (state.IS_MUTED) return;
    try {
        const now = Tone.now();
        // Retro 8-bit "coin insert" double sweep / arcade arpeggio
        retroArcadeStartSound.triggerAttackRelease("G4", "16n", now);
        retroArcadeStartSound.triggerAttackRelease("C5", "16n", now + 0.06);
        retroArcadeStartSound.triggerAttackRelease("E5", "16n", now + 0.12);
        retroArcadeStartSound.triggerAttackRelease("G5", "16n", now + 0.18);
        retroArcadeStartSound.triggerAttackRelease("C6", "8n", now + 0.24);
        
        // Shout special robotic voice: "Arcade, START!" -> "アーケード、スタート！"
        setTimeout(() => {
            playRoboticVoice("Arcade, START!");
        }, 120);
    } catch (e) {
        console.error("Arcade Mode Start Sound failure:", e);
    }
}

export function triggerWinArcadeSequence() {
    if (state.IS_MUTED) return;
    try {
        const now = Tone.now();
        retroArcadeWinSound.triggerAttackRelease("G4", "16n", now);
        retroArcadeWinSound.triggerAttackRelease("C5", "16n", now + 0.08);
        retroArcadeWinSound.triggerAttackRelease("E5", "16n", now + 0.16);
        retroArcadeWinSound.triggerAttackRelease("G5", "8n", now + 0.24);
        
        setTimeout(() => {
            playRoboticVoice("GG! GOOD GAME!");
        }, 100);
    } catch (e) {
        console.error("Arcade Win Sound failure:", e);
    }
}

export function triggerGameOverSequence() {
    if (state.IS_MUTED) return;
    try {
        const now = Tone.now();
        retroArcadeStartSound.triggerAttackRelease("C4", "8n", now);
        retroArcadeStartSound.triggerAttackRelease("F#3", "8n", now + 0.15);
        retroArcadeStartSound.triggerAttackRelease("E3", "4n", now + 0.3);
        
        setTimeout(() => {
            playRoboticVoice("GAME OVER!");
        }, 120);
    } catch (e) {
        console.error("Arcade Game Over Sound failure:", e);
    }
}

// --- Dynamic Menu Soundtrack (Tone.js Chiptune Loop & Sweeps) ---
export const menuFilter = new Tone.Filter({
    type: "lowpass",
    frequency: 3800,
    Q: 3.2 // Warmer and smoother resonance Q (less digital resonance whistle)
}).connect(sfxReverb); // Route through space reverb to smooth out rough chiptune grains!

// Retro space delay echo feedback connects directly to menuFilter (bitcrusher removed to fix RF static)
export const menuDelay = new Tone.FeedbackDelay("8n.", 0.28).connect(menuFilter);

export const menuMelodySynth = new Tone.Synth({
    oscillator: { type: "square" },
    envelope: { attack: 0.001, decay: 0.06, sustain: 0.0, release: 0.08 }
}).connect(menuDelay);
menuMelodySynth.volume.value = -34; // Ultra-quiet background chiptune plucks

export const menuBassSynth = new Tone.Synth({
    oscillator: { type: "triangle" },
    envelope: { attack: 0.002, decay: 0.12, sustain: 0.0, release: 0.10 }
}).connect(menuDelay);
menuBassSynth.volume.value = -36; // Cozy, gentle backing triangle bass

// --- Loopable Backing Track Player (Optimized for Web) ---
export const bgMusicPlayer = new Tone.Player({
    url: backingTrackUrl,
    loop: true,
    autostart: false,
    fadeIn: 1.0,
    fadeOut: 1.0
}).connect(menuDelay);
bgMusicPlayer.volume.value = -22; // Quiet background backing track ("volume down a good bit")

let menuSequence = null;
let bassSequence = null;
let isMusicStarted = false;
let gameTransitionTimeoutId = null;

// Autostart fallback if the buffer completes loading AFTER context is initialized and started
bgMusicPlayer.buffer.onload = () => {
    console.log("bgMusicPlayer loaded backing track audio buffer.");
    if (isMusicStarted && !state.IS_MUTED && !state.IS_MUSIC_MUTED) {
        try {
            if (bgMusicPlayer.state !== 'started') {
                bgMusicPlayer.start();
            }
        } catch (e) {
            console.error("bgMusicPlayer autostart after load failed:", e);
        }
    }
};

function createSequences() {
    if (menuSequence && bassSequence) return;

    if (!menuSequence) {
        // High-energy, bouncy, optimistic retro chiptune melody with syncopations and space rests
        const melodyNotes = [
            "B5", null, "G5", "B5", "D6", null, "G5", "B5",
            "C6", null, "G5", "C6", "E6", null, "G5", "C6",
            "D6", null, "A5", "D6", "F#6", null, "F#6", "D6",
            "E6", null, "B5", "G6", "E6", "B5", "D6", null,
            
            "B5", null, "G5", "B5", "D6", null, "G5", "B5",
            "C6", null, "G5", "C6", "E6", null, "G5", "C6",
            "D6", null, "A5", "D6", "F#6", null, "F#6", "D6",
            "G6", "F#6", "E6", "D6", "B5", "A5", "G5", null
        ];
        
        menuSequence = new Tone.Sequence((time, note) => {
            if (note && !state.IS_MUTED && !state.IS_MUSIC_MUTED) {
                try {
                    // Trigger with shorter 16n length to ensure beautiful plucky retro gaps
                    menuMelodySynth.triggerAttackRelease(note, "16n", time);
                } catch (e) {}
            }
        }, melodyNotes, "8n"); // Upbeat bouncy 8th steps
    }

    if (!bassSequence) {
        // Highly syncopated, cozy backing walking triangle-bassline typical of NES soundchip
        const bassNotes = [
            "G2", null, "G2", null, "B2", null, "B2", null,
            "C2", null, "C2", null, "E2", null, "E2", null,
            "D2", null, "D2", null, "F#2", null, "F#2", null,
            "E2", null, "E2", "B2", "E3", null, "D3", null,
            
            "G2", null, "G2", null, "B2", null, "B2", null,
            "C2", null, "C2", null, "E2", null, "E2", null,
            "D2", null, "D2", null, "F#2", null, "F#2", null,
            "G2", null, "D2", null, "G1", null, null, null
        ];
        
        bassSequence = new Tone.Sequence((time, note) => {
            if (note && !state.IS_MUTED && !state.IS_MUSIC_MUTED) {
                try {
                    // Triangle bass triggered with cozy 8n duration
                    menuBassSynth.triggerAttackRelease(note, "8n", time);
                } catch (e) {}
            }
        }, bassNotes, "8n"); // Syncopated 8th steps
    }
}

function ensureSequencesActive() {
    try {
        if (state.IS_MUTED) return;

        // Keep the old sequences disabled for now while testing backingtrack2
        // createSequences();

        if (Tone.context.state === 'running') {
            if (Tone.Transport.state !== 'started') {
                Tone.Transport.position = 0; // reset transport position
                Tone.Transport.start();
            }
            if (bgMusicPlayer.loaded) {
                if (bgMusicPlayer.state !== 'started') {
                    bgMusicPlayer.start();
                }
            }
        }
    } catch (e) {
        console.error("ensureSequencesActive failed:", e);
    }
}

export function startMenuMusic() {
    if (state.IS_MUTED) return;

    try {
        // Trigger AudioContext resume synchronously inside the user gesture stack
        if (Tone.context.state !== 'running') {
            Tone.start()
                .then(() => {
                    console.log("Tone.js context resumed successfully in startMenuMusic.");
                    ensureSequencesActive();
                })
                .catch(e => console.error("Tone.start failed:", e));
        }

        ensureSequencesActive();

        if (state.IS_MUSIC_MUTED) {
            stopMenuMusic();
            return;
        }

        if (!isMusicStarted) {
            // Keep old synths silent
            menuMelodySynth.volume.setValueAtTime(-100, Tone.now());
            menuBassSynth.volume.setValueAtTime(-100, Tone.now());
            // Play backing track quiet
            bgMusicPlayer.volume.setValueAtTime(-22, Tone.now());
            isMusicStarted = true;
            console.log("Backing track 2 loop started synchronously inside user gesture.");
        } else {
            // Keep old synths silent
            menuMelodySynth.volume.setValueAtTime(-100, Tone.now());
            menuBassSynth.volume.setValueAtTime(-100, Tone.now());
            // Ensure backing track remains quiet and play if loaded
            bgMusicPlayer.volume.setValueAtTime(-22, Tone.now());
            if (bgMusicPlayer.loaded && bgMusicPlayer.state !== 'started') {
                bgMusicPlayer.start();
            }
            console.log("Backing track 2 loop verified running.");
        }
    } catch (e) {
        console.error("Failed to start menu music loop:", e);
    }
}

export function stopMenuMusic() {
    try {
        menuMelodySynth.volume.setValueAtTime(-100, Tone.now());
        menuBassSynth.volume.setValueAtTime(-100, Tone.now());
        bgMusicPlayer.volume.setValueAtTime(-100, Tone.now());
        if (bgMusicPlayer.state === 'started') {
            bgMusicPlayer.stop();
        }
    } catch (e) {
        console.error("Stopping menu music failed:", e);
    }
}

export function fadeAndStopMenuMusic() {
    try {
        if (gameTransitionTimeoutId) {
            clearTimeout(gameTransitionTimeoutId);
            gameTransitionTimeoutId = null;
        }
        
        // Muffle/filter down to low-pass 120Hz over 0.4 seconds
        menuFilter.frequency.rampTo(120, 0.4);
        menuMelodySynth.volume.rampTo(-100, 0.4);
        menuBassSynth.volume.rampTo(-100, 0.4);
        bgMusicPlayer.volume.rampTo(-100, 0.4);
        
        // Lock silent state after sweep completes
        gameTransitionTimeoutId = setTimeout(() => {
            stopMenuMusic();
            gameTransitionTimeoutId = null;
        }, 420);
    } catch (e) {
        console.error("fadeAndStopMenuMusic failed:", e);
        stopMenuMusic();
    }
}

export function transitionMusicToGame() {
    try {
        menuFilter.frequency.rampTo(120, 0.35);
        menuMelodySynth.volume.rampTo(-100, 0.35);
        menuBassSynth.volume.rampTo(-100, 0.35);
        bgMusicPlayer.volume.rampTo(-100, 0.35);
    } catch (e) {
        console.error("Low-pass transition to game failed:", e);
        stopMenuMusic();
    }
}

export function transitionMusicToMenu() {
    if (state.IS_MUTED) return;
    if (gameTransitionTimeoutId) {
        clearTimeout(gameTransitionTimeoutId);
        gameTransitionTimeoutId = null;
    }
    if (state.IS_MUSIC_MUTED) {
        stopMenuMusic();
        return;
    }
    try {
        // Ensure loop is cued and running
        startMenuMusic();
        
        // Restore volumes and open filter back up smoothly
        menuFilter.frequency.setValueAtTime(120, Tone.now());
        menuFilter.frequency.rampTo(3800, 0.45);
        
        menuMelodySynth.volume.rampTo(-100, 0.45);
        menuBassSynth.volume.rampTo(-100, 0.45);
        bgMusicPlayer.volume.rampTo(-22, 0.45);
    } catch (e) {
        console.error("Low-pass transition back to menu failed:", e);
    }
}

// --- Continuous Rubber Band Stretch SFX ---
export const stretchSynth = new Tone.Synth({
    oscillator: { type: "sine" },
    envelope: { attack: 0.15, decay: 0.1, sustain: 1.0, release: 0.2 }
}).connect(sfxReverb); // route through reverb for organic smoothness
stretchSynth.volume.value = -100;

// High-fidelity mechanical tension vibrato/tremolo wobble
export const stretchLFO = new Tone.LFO({
    frequency: 8.5, // 8.5Hz rapid retro wobbling flutter
    min: -30,       // ±30 cents detuning
    max: 30
}).start();
stretchLFO.connect(stretchSynth.detune);

let stretchActive = false;

export function startStretchSound() {
    if (state.IS_MUTED) return;
    try {
        if (!stretchActive) {
            // Guarantee context is running during gesture execution context
            if (Tone.context.state !== 'running') {
                Tone.start().catch(e => console.error("Tone.start failed in startStretchSound:", e));
            }
            
            // Standard sine wave pullback for all skins!
            stretchSynth.oscillator.type = "sine";
            stretchLFO.frequency.setValueAtTime(8.5, Tone.now());
            
            // Smoothly open up volume from silence to prevent clicking pops on start
            stretchSynth.volume.setValueAtTime(-100, Tone.now());
            stretchSynth.triggerAttack("E3", Tone.now());
            stretchSynth.volume.rampTo(-26, 0.05);
            stretchActive = true;
        }
    } catch (e) {
        console.error("startStretchSound failed:", e);
    }
}

export function updateStretchSound(powerRatio) {
    if (state.IS_MUTED || !stretchActive) return;
    try {
        // Slide pitch exponentially from phone-audible E3 (164.81Hz) to B4 (494.4Hz)
        const freq = 164.81 * Math.pow(3.0, powerRatio); 
        
        // Smoothly ramp volume from -26dB to -18dB (a bit quieter and non-overpowering)
        const volumeDb = -26 + powerRatio * 8; 
        
        // Use 50ms smoothing ramps to eliminate sample clipping/glitches
        stretchSynth.frequency.rampTo(freq, 0.05);
        stretchSynth.volume.rampTo(volumeDb, 0.05);
    } catch (e) {
        console.error("updateStretchSound failed:", e);
    }
}

export function stopStretchSound() {
    try {
        if (stretchActive) {
            // Smoothly ramp volume to silent over 50ms before releasing voice to prevent click popping
            stretchSynth.volume.rampTo(-100, 0.05);
            stretchSynth.triggerRelease(Tone.now() + 0.05);
            stretchActive = false;
        }
    } catch (e) {
        console.error("stopStretchSound failed:", e);
    }
}

// --- Out of Bounds Chiptune Slide ---
export const oobSynth = new Tone.Synth({
    oscillator: { type: "sawtooth" },
    envelope: { attack: 0.005, decay: 0.2, sustain: 0, release: 0.1 }
}).connect(sfxDelay);
oobSynth.volume.value = -12;

export function playOutOfBoundsSound() {
    if (state.IS_MUTED) return;
    try {
        const now = Tone.now();
        oobSynth.triggerAttackRelease("G3", "8n", now);
        oobSynth.frequency.setValueAtTime(196.00, now);
        oobSynth.frequency.exponentialRampToValueAtTime(49.00, now + 0.12); // sharp downward chiptune sweep!
    } catch (e) {
        console.error("OOB sound failure:", e);
    }
}

export function playShotCancelledSound() {
    if (state.IS_MUTED) return;
    try {
        const now = Tone.now();
        cannotAffordSound.triggerAttackRelease("C3", "16n", now);
        cannotAffordSound.frequency.setValueAtTime(130.81, now);
        cannotAffordSound.frequency.exponentialRampToValueAtTime(65.41, now + 0.1); // downward slide C3 -> C2
    } catch (e) {
        console.error("Shot cancelled sound failure:", e);
    }
}

// --- Navigation Click Beeps ---
export const clickBeepSound = new Tone.Synth({
    oscillator: { type: "square" },
    envelope: { attack: 0.001, decay: 0.04, sustain: 0, release: 0.04 }
}).connect(sfxDelay);
clickBeepSound.volume.value = -18;

export function playMenuClickSound() {
    if (state.IS_MUTED) return;
    try {
        const now = Tone.now();
        clickBeepSound.triggerAttackRelease("C5", "32n", now);
    } catch (e) {
        console.error("Click sound failure:", e);
    }
}

// --- Menu Button Speech Vocoder V/Os ---
export function playVoiceOver(text) {
    if (state.IS_MUTED) return;
    try {
        playRoboticVoice(text);
    } catch (e) {}
}

const RANDOM_PHRASES = [
    "よっしゃ！",
    "いくぞ！",
    "オッケー！",
    "スペース！",
    "キター！",
    "ファイト！"
];

export function playRandomFunPhrase() {
    if (state.IS_MUTED) return;
    try {
        const randomIndex = Math.floor(Math.random() * RANDOM_PHRASES.length);
        playRoboticVoice(RANDOM_PHRASES[randomIndex]);
    } catch (e) {
        console.error("Failed to play random phrase:", e);
    }
}

// Specialized chiptune sound chimes for power-up activations & deactivations
export function playPowerUpSelectSound(type, isActive) {
    if (state.IS_MUTED) return;
    try {
        const now = Tone.now();
        if (type === 'trajectoryGhost' || type === 'trajectoryLengthener') {
            if (isActive) {
                // Ascending space eerie sweep arpeggio
                retroArcadeStartSound.triggerAttackRelease("C5", "32n", now);
                retroArcadeStartSound.triggerAttackRelease("E5", "32n", now + 0.05);
                retroArcadeStartSound.triggerAttackRelease("G5", "32n", now + 0.1);
                retroArcadeStartSound.triggerAttackRelease("B5", "16n", now + 0.15);
            } else {
                // Descending eerie sweep
                retroArcadeStartSound.triggerAttackRelease("B5", "32n", now);
                retroArcadeStartSound.triggerAttackRelease("G5", "32n", now + 0.05);
                retroArcadeStartSound.triggerAttackRelease("E5", "32n", now + 0.1);
                retroArcadeStartSound.triggerAttackRelease("C5", "16n", now + 0.15);
            }
        } else if (type === 'gravityGrid') {
            if (isActive) {
                // Ascending digital space-time warp sweep
                retroArcadeStartSound.triggerAttackRelease("D4", "32n", now);
                retroArcadeStartSound.triggerAttackRelease("A4", "32n", now + 0.05);
                retroArcadeStartSound.triggerAttackRelease("D5", "16n", now + 0.1);
            } else {
                // Descending digital warp sweep
                retroArcadeStartSound.triggerAttackRelease("D5", "32n", now);
                retroArcadeStartSound.triggerAttackRelease("A4", "32n", now + 0.05);
                retroArcadeStartSound.triggerAttackRelease("D4", "16n", now + 0.1);
            }
        } else if (type === 'stickyBall') {
            if (isActive) {
                // Sticky squishy low slide down
                thudBounceSynth.triggerAttackRelease("E3", "8n", now);
                thudBounceSynth.frequency.setValueAtTime(164.81, now);
                thudBounceSynth.frequency.exponentialRampToValueAtTime(82.41, now + 0.12);
            } else {
                // Squishy slide up
                thudBounceSynth.triggerAttackRelease("E2", "8n", now);
                thudBounceSynth.frequency.setValueAtTime(82.41, now);
                thudBounceSynth.frequency.exponentialRampToValueAtTime(164.81, now + 0.12);
            }
        } else if (type === 'linearLaser') {
            if (isActive) {
                // High zappy laser power-up chime
                clickBeepSound.triggerAttackRelease("E5", "32n", now);
                clickBeepSound.triggerAttackRelease("G#5", "32n", now + 0.04);
                clickBeepSound.triggerAttackRelease("B5", "16n", now + 0.08);
            } else {
                // Descending laser de-arm beep
                clickBeepSound.triggerAttackRelease("B4", "16n", now);
            }
        } else if (type === 'homingBall') {
            if (isActive) {
                // Target lock double beep
                clickBeepSound.triggerAttackRelease("A5", "32n", now);
                clickBeepSound.triggerAttackRelease("A5", "32n", now + 0.08);
            } else {
                // Lock unlock single low beep
                clickBeepSound.triggerAttackRelease("E4", "16n", now);
            }
        } else if (type === 'multiBall') {
            if (isActive) {
                // Rapid three-beep ascending burst
                clickBeepSound.triggerAttackRelease("C5", "32n", now);
                clickBeepSound.triggerAttackRelease("F5", "32n", now + 0.05);
                clickBeepSound.triggerAttackRelease("A5", "16n", now + 0.1);
            } else {
                // Single down burst beep
                clickBeepSound.triggerAttackRelease("G4", "16n", now);
            }
        } else if (type === 'oobShield') {
            if (isActive) {
                // High futuristic shield bubble sound
                clickBeepSound.triggerAttackRelease("C6", "32n", now);
                clickBeepSound.triggerAttackRelease("E6", "16n", now + 0.04);
            } else {
                // Lower drop off arm beep
                clickBeepSound.triggerAttackRelease("G4", "16n", now);
            }
        }
    } catch (e) {
        console.error("Power-up select sound failure:", e);
    }
}
